/**
 * @baba/i18n · supported locales registry
 *
 * Only add a locale here once it has a `strings/<code>.ts` catalog. The
 * picker UI iterates this list. Direction matters for RTL layout switches.
 *
 * Priority order for translation rollout (from our demographics research):
 *   1. he — ~7M native Israelis, highest willingness-to-pay
 *   2. fr — ~450K French Jewish community, active Aliyah pipeline
 *   3. ru — ~1M Russian-speakers in Israel + FSU diaspora
 *   4. es — ~250K Jewish community across Argentina / Mexico / Spain
 *   5. pt — Brazil (~90K Jewish community)
 *   6. de — ~120K German Jewish community
 */

export type LocaleCode = 'en' | 'he' | 'fr' | 'ru' | 'es' | 'pt' | 'de'

export interface LocaleMeta {
  code: LocaleCode
  /** Native-language name: what the picker shows. */
  native: string
  /** English name: what we show to English-speaking admins/logs. */
  english: string
  dir: 'ltr' | 'rtl'
  /** Which Stripe Checkout locale to request (docs.stripe.com/js). */
  stripeLocale: string
  /** Whether we actually have translations populated yet. */
  ready: boolean
}

export const LOCALES: LocaleMeta[] = [
  { code: 'en', native: 'English',   english: 'English',      dir: 'ltr', stripeLocale: 'en',    ready: true  },
  { code: 'he', native: 'עברית',     english: 'Hebrew',       dir: 'rtl', stripeLocale: 'he',    ready: true  },
  { code: 'fr', native: 'Français',  english: 'French',       dir: 'ltr', stripeLocale: 'fr',    ready: false },
  { code: 'ru', native: 'Русский',   english: 'Russian',      dir: 'ltr', stripeLocale: 'ru',    ready: false },
  { code: 'es', native: 'Español',   english: 'Spanish',      dir: 'ltr', stripeLocale: 'es',    ready: false },
  { code: 'pt', native: 'Português', english: 'Portuguese',   dir: 'ltr', stripeLocale: 'pt-BR', ready: false },
  { code: 'de', native: 'Deutsch',   english: 'German',       dir: 'ltr', stripeLocale: 'de',    ready: false },
]

export const DEFAULT_LOCALE: LocaleCode = 'en'

export function isSupported(code: string | null | undefined): code is LocaleCode {
  if (!code) return false
  return LOCALES.some((l) => l.code === code)
}

export function localeMeta(code: LocaleCode): LocaleMeta {
  return LOCALES.find((l) => l.code === code) || LOCALES[0]
}

/** Locales that currently have translations populated (show in picker). */
export function readyLocales(): LocaleMeta[] {
  return LOCALES.filter((l) => l.ready)
}
